import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSignIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
export function BudgetVsActualReport({
  filters,
  locale
}) {
  // Sample data for budget vs actual report
  const budgetData = [{
    department: 'Marketing',
    budget: 150000,
    actual: 142500,
    variance: -7500
  }, {
    department: 'Sales',
    budget: 200000,
    actual: 215000,
    variance: 15000
  }, {
    department: 'IT',
    budget: 300000,
    actual: 285000,
    variance: -15000
  }, {
    department: 'Operations',
    budget: 250000,
    actual: 248000,
    variance: -2000
  }, {
    department: 'HR',
    budget: 100000,
    actual: 98500,
    variance: -1500
  }, {
    department: 'Finance',
    budget: 120000,
    actual: 115000,
    variance: -5000
  }];
  const monthlyData = [{
    month: 'Jan',
    budget: 95000,
    actual: 92000
  }, {
    month: 'Feb',
    budget: 100000,
    actual: 98500
  }, {
    month: 'Mar',
    budget: 110000,
    actual: 115000
  }, {
    month: 'Apr',
    budget: 105000,
    actual: 107000
  }, {
    month: 'May',
    budget: 115000,
    actual: 110000
  }, {
    month: 'Jun',
    budget: 120000,
    actual: 118000
  }];
  const totalBudget = budgetData.reduce((sum, item) => sum + item.budget, 0);
  const totalActual = budgetData.reduce((sum, item) => sum + item.actual, 0);
  const totalVariance = totalActual - totalBudget;
  const variancePercentage = totalVariance / totalBudget * 100;
  return <div className="p-6" data-id="element-15778">
      <div className="mb-8" data-id="element-15779">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-15780">
          Budget vs Actual Spend Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-15781">
          Compare budgeted amounts against actual spend by department, GL code,
          or entity
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-15782">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15783">
          <div className="p-5" data-id="element-15784">
            <div className="flex items-center" data-id="element-15785">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-15786">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-15787" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15788">
                <dl data-id="element-15789">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15790">
                    Total Budget
                  </dt>
                  <dd data-id="element-15791">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15792">
                      {formatCurrency(totalBudget, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15793">
          <div className="p-5" data-id="element-15794">
            <div className="flex items-center" data-id="element-15795">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-15796">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-15797" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15798">
                <dl data-id="element-15799">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15800">
                    Total Actual
                  </dt>
                  <dd data-id="element-15801">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15802">
                      {formatCurrency(totalActual, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15803">
          <div className="p-5" data-id="element-15804">
            <div className="flex items-center" data-id="element-15805">
              <div className={`flex-shrink-0 ${totalVariance < 0 ? 'bg-green-500' : 'bg-red-500'} rounded-md p-3`} data-id="element-15806">
                {totalVariance < 0 ? <TrendingDownIcon className="h-6 w-6 text-white" data-id="element-15807" /> : <TrendingUpIcon className="h-6 w-6 text-white" data-id="element-15808" />}
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15809">
                <dl data-id="element-15810">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15811">
                    Variance
                  </dt>
                  <dd data-id="element-15812">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15813">
                      {formatCurrency(totalVariance, 'USD', locale)} (
                      {variancePercentage.toFixed(1)}%)
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Department Chart */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-15814">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15815">
          Budget vs Actual by Department
        </h4>
        <div className="h-80" data-id="element-15816">
          <ResponsiveContainer width="100%" height="100%" data-id="element-15817">
            <BarChart data={budgetData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-15818">
              <CartesianGrid strokeDasharray="3 3" data-id="element-15819" />
              <XAxis dataKey="department" data-id="element-15820" />
              <YAxis data-id="element-15821" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-15822" />
              <Legend data-id="element-15823" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-15824" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-15825" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Monthly Trend */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-15826">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15827">
          Monthly Budget vs Actual Trend
        </h4>
        <div className="h-80" data-id="element-15828">
          <ResponsiveContainer width="100%" height="100%" data-id="element-15829">
            <BarChart data={monthlyData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-15830">
              <CartesianGrid strokeDasharray="3 3" data-id="element-15831" />
              <XAxis dataKey="month" data-id="element-15832" />
              <YAxis data-id="element-15833" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-15834" />
              <Legend data-id="element-15835" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-15836" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-15837" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Department Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-15838">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-15839">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15840">
            Department Budget Details
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-15841">
            Detailed breakdown by department
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-15842">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-15843">
            <thead className="bg-gray-50" data-id="element-15844">
              <tr data-id="element-15845">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15846">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15847">
                  Budget
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15848">
                  Actual
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15849">
                  Variance
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15850">
                  % Variance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-15851">
              {budgetData.map((dept, index) => <tr key={index} data-id="element-15852">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15853">
                    {dept.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-15854">
                    {formatCurrency(dept.budget, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-15855">
                    {formatCurrency(dept.actual, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-15856">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-15857">
                      {formatCurrency(dept.variance, 'USD', locale)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-15858">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-15859">
                      {(dept.variance / dept.budget * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}